// NPM imports
import React, { useEffect, useMemo } from 'react';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { IntlProvider } from 'react-intl';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { ProgressCircular, Button } from '@americanexpress/dls-react';
import { useSelector } from 'react-redux';
import { useFetchye } from 'fetchye';

// Local imports
import childRoutes from '../childRoutes';
import PasskeyRegistration from './PasskeyRegistration/PasskeyRegistration';
import PasskeyAssertion from './PasskeyAssertion/PasskeyAssertion';
import SHARED from '../utils/sharedConstants';
import ENDPOINTS from '../utils/endpoints';
import buildFetchOptions from '../utils/fetchOptions';
import useSpcBbkCompatibility from '../hooks/useSpcBbkCompatibility';
import styles from './main.scss';
import fallbackLanguageData from '../../locale/en-US.json';

const Main = ({ params, languageData, locale }) => {
  const cardMetaData = params?.uniqueId;
  const { FLOW_TYPE } = SHARED;
  const { CEREMONY_SESSIONS } = ENDPOINTS;

  const bffApiUrl = useSelector((state) => state.getIn(['config', 'bffApiUrl']));
  const { deviceContext, isSpcBbkLoading } = useSpcBbkCompatibility();
  const isReady = Boolean(cardMetaData && !isSpcBbkLoading && bffApiUrl);
  const ceremonyUrl = useMemo(
    () => (isReady ? `${bffApiUrl}${CEREMONY_SESSIONS}` : null),
    [bffApiUrl, CEREMONY_SESSIONS, isReady]
  );

  const requestBody = useMemo(() => ({
    uniqueId: cardMetaData,
    deviceContext: deviceContext || {},
  }), [cardMetaData, deviceContext]);

  const { data: sessionData, error: sessionError, run: retrySession } = useFetchye(ceremonyUrl,
    buildFetchOptions({ body: requestBody, defer: !isReady })
  );

  const resolvedLanguageData = useMemo(
    () => (Object.keys(languageData || {}).length > 0 ? languageData : fallbackLanguageData),
    [languageData]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.key === 'F12'
        || ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C'))
        || ((e.ctrlKey || e.metaKey) && (e.key === 'u' || e.key === 'U'))
      ) {
        e.preventDefault();
      }
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  const {
    sessionId,
    ceremonyType,
    challenge,
    credentialIds,
    credentialCount,
    maskedCardNumber,
    token,
  } = sessionData?.body || {};

  const renderFlow = () => {
    if (!cardMetaData) {
      return (
        <div role="alert" className={styles['main-container']}>
          <p>{resolvedLanguageData['main.error.missingCardMetaData']}</p>
        </div>
      );
    }

    if (sessionError || sessionData?.status >= 400) {
      return (
        <div role="alert" className={styles['main-container']}>
          <p>{resolvedLanguageData['passkeyAssertion.error.message']}</p>
          <Button variant="primary" onClick={retrySession}>
            {resolvedLanguageData['passkeyAssertion.error.retryWithPasskey']}
          </Button>
        </div>
      );
    }

    if (!sessionData) {
      return (
        <div className={styles['main-container']}>
          <ProgressCircular />
        </div>
      );
    }

    if (ceremonyType === FLOW_TYPE.AUTHENTICATION) {
      return (
        <PasskeyAssertion
          sessionData={{
            sessionId,
            challenge,
            credentialIds,
            token,
          }}
        />
      );
    }
    return (
      <PasskeyRegistration
        sessionData={{
          sessionId,
          challenge,
          credentialIds,
          credentialCount,
          maskedCardNumber,
          token,
        }}
      />
    );
  };

  return (
    <IntlProvider locale={locale} messages={resolvedLanguageData}>
      <Helmet
        title="American Express SafeKey"
        htmlAttributes={{ lang: locale }}
        link={[
          {
            rel: 'stylesheet',
            href: 'https://www.aexp-static.com/cdaas/dls/packages/@americanexpress/dls/7.15.0/stylesheets/dls-core.min.css',
          },
          {
            rel: 'icon',
            href: 'https://www.americanexpress.com/favicon.ico',
          },
        ]}
      />
      {renderFlow()}
    </IntlProvider>
  );
};

export const TestableMain = Main;

Main.propTypes = {
  languageData: PropTypes.shape({}).isRequired, // no need to restate all the keys in the lang pack
  locale: PropTypes.string.isRequired,
  params: PropTypes.shape({
    uniqueId: PropTypes.string,
  }),
  uniqueId: PropTypes.string,
};

// Read about childRoutes:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/Routing.md#childroutes
Main.childRoutes = childRoutes;

// Read about appConfig:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/App-Configuration.md
/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line global-require -- require needs to be inside browser check
  Main.appConfig = require('../appConfig').default;
}

// Read the Vault-injected `test` secret directly from process.env on the server to verify
// the integration. It is intentionally not routed through Redux/provideStateConfig so the
// raw value never reaches the client bundle. The One App server loads it from the Vault
// dotenv file referenced by DOTENV_CONFIG_PATH (see helm values). This runs when the server
// evaluates the module bundle, so it does not depend on a page request being rendered.
// Logged through `global.console` rather than the bare `console` identifier: One App wraps
// console on the server so entries land in the pod logs with the same shape as its own
// (which is what the log pipeline indexes), while the indirection keeps terser's
// `drop_console` — enabled for production bundles by one-app-bundler — from stripping the
// call out of the deployed node bundle.
export const logVaultTestSecret = () => {
  if (global.BROWSER) return;
  const testValue = process.env.test;
  if (testValue) {
    global.console.info(`[axp-passkey-auth-root] Vault "test" secret value: ${testValue}`);
  } else {
    const reason = typeof testValue === 'undefined'
      ? 'process.env.test is undefined (check Vault onboarding and DOTENV_CONFIG_PATH mount)'
      : 'process.env.test is set but empty';
    global.console.warn(`[axp-passkey-auth-root] Vault "test" secret not found: ${reason}`);
  }
};

/* istanbul ignore next */
if (!global.BROWSER) {
  logVaultTestSecret();
}

export const loadModuleData = ({ store: { dispatch } }) => dispatch(
  loadLanguagePack('axp-passkey-auth-root', { fallbackLocale: 'en-US' })
);

Main.holocron = {
  name: 'axp-passkey-auth-root',
  loadModuleData,
};

export default oneAppModuleWrapper('axp-passkey-auth-root')(Main);
